import { useEffect, useRef, useState } from "react";
import { Video, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * DailyEmberButton — main-menu rewarded-ad surface. Once per calendar day, the
 * player can watch a 5-second ad to earn +1 bonus_life. Hidden if already
 * claimed today or the ad system is capped.
 *
 * See MONETIZATION_STRATEGY §3.2 (surface #3).
 */
export default function DailyEmberButton({ onClaimed }) {
  const [config, setConfig] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | playing | granting
  const [countdown, setCountdown] = useState(5);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/ads/config")
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        // Infer daily_bonus already used from remaining vs cap — best-effort;
        // authoritative gate is on the backend which returns 429.
      })
      .catch(() => { /* not authed — hide */ });
    return () => { cancelled = true; if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  if (!config || claimed || (config.remaining ?? 0) <= 0) return null;

  const startAd = () => {
    setPhase("playing");
    setCountdown(5);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          grant();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const grant = async () => {
    setPhase("granting");
    try {
      const ad_token = `mock_daily_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const res = await api.post("/ads/reward/daily-ember", {
        ad_token,
        placement: "daily_bonus",
      });
      if (res?.ok) {
        toast.success("+1 ember added to your fire.");
        setClaimed(true);
        onClaimed?.(res);
      } else {
        throw new Error("Reward failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Daily bonus unavailable.";
      if (/already claimed/i.test(detail)) setClaimed(true);
      toast.error(detail);
      setPhase("idle");
    }
  };

  if (phase === "playing") {
    return (
      <div
        className="panel px-3 py-2 flex items-center gap-2 min-w-[8rem] justify-center"
        data-testid="daily-ember-playing"
        style={{ borderColor: "#7fb3ff" }}
      >
        <span className="text-[10px] tracking-[0.3em] ash-text">DEV AD ·</span>
        <span className="font-heading" style={{ color: "#7fb3ff" }}>{countdown}s</span>
      </div>
    );
  }

  if (phase === "granting") {
    return (
      <div className="panel px-3 py-2 flex items-center gap-2" data-testid="daily-ember-granting">
        <Loader2 size={12} className="animate-spin" />
        <span className="text-[10px] tracking-[0.3em] ash-text">GRANTING...</span>
      </div>
    );
  }

  return (
    <button
      onClick={startAd}
      data-testid="daily-ember-btn"
      className="panel px-3 py-2 flex items-center gap-2 hover:border-[#7fb3ff] transition-colors relative"
      title="Watch a short ad to earn a free ember (once per day)"
      style={{ borderColor: "rgba(80,140,220,0.35)" }}
    >
      <Video size={14} style={{ color: "#7fb3ff" }} />
      <span className="text-[10px] tracking-[0.3em] ash-text">+1 EMBER</span>
      <Sparkles size={10} className="gold-text opacity-70" />
    </button>
  );
}
